import * as Ariakit from "@ariakit/react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Tooltip } from "@mui/joy";
import Fuse from "fuse.js";
import { enqueueSnackbar } from "notistack";
import { startTransition, useEffect, useMemo, useState } from "react";
import "../combobox.css";
import { supabase } from "../utils/supabase";
import ScheduleIcon from "@mui/icons-material/Schedule";

const ComboBox = ({
  selectedBodyPart,
  customExercises,
  setCustomExercises,
  handleKeyPress,
  exerciseDataStorage,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [exerciseNames, setExerciseNames] = useState([]);
  const debouncedSearch = useDebounce(searchValue, 200);
  const exerciseData = JSON.parse(localStorage.getItem("exerciseData"));
  const userUniqueExercises = Array.from(
    new Set(exerciseDataStorage.map((exercise) => exercise.exerciseName))
  );

  const isDemoUser =
    localStorage.getItem("userId") === "3cdf8cc0-6f83-4831-98a1-35b36dcd773d";
  localStorage.setItem("searchValue", searchValue);
  const matches = useMemo(() => {
    if (!debouncedSearch) {
      return exerciseNames.sort(
        (a, b) =>
          userUniqueExercises.includes(b.name) -
          userUniqueExercises.includes(a.name)
      );
    }

    const uniqueExerciseNames = Array.from(
      new Set(exerciseNames.map(({ name }) => name))
    );

    const fuseOptions = {
      includeScore: true,
      keys: ["name"],
      threshold: 0.3,
      ignoreLocation: true,
    };

    const fuse = new Fuse(uniqueExerciseNames, fuseOptions);
    const searchResults = fuse.search(debouncedSearch);
    const sortedMatches = searchResults
      .map(({ item }) => exerciseNames.find(({ name }) => name === item))
      .filter(Boolean);

    return sortedMatches.sort(
      (a, b) =>
        userUniqueExercises.includes(b.name) -
        userUniqueExercises.includes(a.name)
    );
  }, [debouncedSearch, exerciseNames, userUniqueExercises]);

  useEffect(() => {
    const names = exerciseData?.filter(
      (exercise) => exercise.body_part === selectedBodyPart
    );

    setExerciseNames(names);
  }, [selectedBodyPart]);

  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  async function addCustomExercise() {
    const customExercise = {
      name: searchValue.toLowerCase().trim(),
      body_part: selectedBodyPart,
      isCustom: true,
    };
    if (isDemoUser) {
      enqueueSnackbar("Custom exercise saved!", { variant: "success" });
      setExerciseNames((prevExerciseNames) => [
        ...prevExerciseNames,
        customExercise,
      ]);
      setCustomExercises((prevCustomExercises) => [
        ...prevCustomExercises,
        customExercise,
      ]);
      if (customExercises) {
        localStorage.setItem(
          "exerciseData",
          JSON.stringify([...exerciseData, customExercise])
        );
      }
    } else {
      try {
        // Update the user data with the new exercise appended to customExercises
        const { data, error } = await supabase.auth.updateUser({
          data: {
            customExercises: [...customExercises, customExercise],
          },
        });

        if (error) {
          // Handle error updating user data
          console.error("Error updating user data:", error.message);
        } else {
          // Handle success
          enqueueSnackbar("Custom exercise saved!", { variant: "success" });
          setExerciseNames((prevExerciseNames) => [
            ...prevExerciseNames,
            customExercise,
          ]);
          setCustomExercises((prevCustomExercises) => [
            ...prevCustomExercises,
            customExercise,
          ]);
          if (customExercises) {
            localStorage.setItem(
              "exerciseData",
              JSON.stringify([...exerciseData, customExercise])
            );
          }
        }
      } catch (error) {
        // Handle unexpected errors
        console.error("Unexpected error:", error.message);
      }
    }
  }

  async function deleteCustomExercise(exerciseName) {
    try {
      if (isDemoUser) {
        // Demo user case: No need to update Supabase, only local updates
        enqueueSnackbar("Custom exercise deleted!", { variant: "success" });

        // Update state and localStorage with the modified customExercises
        setExerciseNames((prevExerciseNames) =>
          prevExerciseNames.filter((exercise) => exercise.name !== exerciseName)
        );

        // Filter the exerciseData in localStorage based on updatedCustomExercises
        const filteredLocalStorageData = exerciseData.filter(
          (exercise) => exerciseName !== exercise.name
        );

        localStorage.setItem(
          "exerciseData",
          JSON.stringify([...filteredLocalStorageData])
        );
      } else {
        // Non-demo user case: Update Supabase and perform local updates
        const { data: userData, error: userError } =
          await supabase.auth.getUser();

        if (userError) {
          // Handle error getting user data
          console.error("Error getting user data:", userError.message);
          return;
        }

        // Filter out the exercise with the given name from customExercises
        const updatedCustomExercises =
          userData.user.user_metadata.customExercises.filter(
            (exercise) => exercise.name !== exerciseName
          );

        // Update the user data with the modified customExercises
        const { data, error } = await supabase.auth.updateUser({
          data: {
            customExercises: updatedCustomExercises,
          },
        });

        if (error) {
          // Handle error updating user data
          console.error("Error updating user data:", error.message);
        } else {
          // Handle success
          enqueueSnackbar("Custom exercise deleted!", { variant: "success" });

          // Update state and localStorage with the modified customExercises
          setExerciseNames((prevExerciseNames) =>
            prevExerciseNames.filter(
              (exercise) => exercise.name !== exerciseName
            )
          );

          // Filter the exerciseData in localStorage based on updatedCustomExercises
          const filteredLocalStorageData = exerciseData.filter(
            (exercise) => exerciseName !== exercise.name
          );

          localStorage.setItem(
            "exerciseData",
            JSON.stringify([...filteredLocalStorageData])
          );
        }
      }
    } catch (error) {
      // Handle unexpected errors
      console.error("Unexpected error:", error.message);
    }
  }

  return (
    <Ariakit.ComboboxProvider
      setValue={(value) => {
        startTransition(() => {
          setSearchValue(value);
        });
      }}
    >
      <div className="combobox-wrapper">
        <Ariakit.Combobox
          placeholder="Select exercise..."
          className="combobox"
          value={searchValue}
          tabIndex="1"
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => {
            handleKeyPress(e);
          }}
        />
        <Ariakit.ComboboxCancel className="button secondary combobox-cancel" />
      </div>
      <Ariakit.ComboboxPopover
        gutter={4}
        sameWidth
        className="popover overflow-x-hidden"
      >
        {matches.length < 5 && (
          <div
            onClick={addCustomExercise}
            className="flex items-center combobox-item w-full"
          >
            <Ariakit.ComboboxItem
              setValueOnClick={false}
              value="Save custom exercise"
              className="w-full"
            />

            <AddCircleOutlineIcon />
          </div>
        )}
        {matches.map((value) =>
          value.isCustom ? (
            <div key={value.name} className="flex combobox-item custom w-full">
              <Ariakit.ComboboxItem
                value={value.name}
                className="exercise-name"
              />

              <Tooltip variant="plain" title="Remove custom exercise">
                <RemoveCircleOutlineIcon
                  onClick={() => deleteCustomExercise(value.name)}
                  sx={{
                    cursor: "pointer",
                    alignSelf: "flex-end",
                  }}
                />
              </Tooltip>
              {userUniqueExercises.includes(value.name) && (
                <Tooltip variant="plain" title="Recent exercise">
                  <ScheduleIcon sx={{ alignSelf: "flex-end" }} />
                </Tooltip>
              )}
            </div>
          ) : userUniqueExercises.includes(value.name) ? (
            <div
              key={value.name}
              className="flex items-center combobox-item w-full"
            >
              <Ariakit.ComboboxItem
                key={value.name}
                value={value.name}
                className="w-full"
              />

              <Tooltip variant="plain" title="Recent exercise">
                <ScheduleIcon />
              </Tooltip>
            </div>
          ) : (
            <Ariakit.ComboboxItem
              key={value.name}
              value={value.name}
              className="combobox-item"
            />
          )
        )}
      </Ariakit.ComboboxPopover>
    </Ariakit.ComboboxProvider>
  );
};

export default ComboBox;
